import React, { useEffect, useRef } from "react";
import "./App.css";
import howToGif from "./assets/how-to.gif";

function App() {
    const quizPlayerIframeRef = useRef<HTMLIFrameElement>(null);
    const gameIframeRef = useRef<HTMLIFrameElement>(null);
    const quizPlayerContainerRef = useRef<HTMLDivElement>(null);

    const MIN_ANSWER_INTERVAL = 50;
    const lastAnswerTime = useRef(0);

    const [ammo, setAmmo] = React.useState(5);
    const [numQuizQuestions, setNumQuizQuestions] = React.useState(null);
    const [numQuizQuestionsAnswered, setNumQuizQuestionsAnswered] =
        React.useState(0);

    useEffect(() => {
        // listen for messages from the iframe
        window.addEventListener("message", (event) => {
            console.log("App.tsx message", event.data);

            // Game events
            if (event.data?.eventType === "START_LEVEL") {
                setGameState("ACTIVE");
            }
            if (event.data?.eventType === "SHOOT") {
                // lose ammo from shooting
                const { newAmmo } = event.data;
                setAmmo(newAmmo);
            }
            if (event.data?.eventType === "PAUSE") {
                console.log("App.tsx PAUSE");
                setShowQuizMobile(true);
                setGameState("PAUSED");
            }
            if (event.data?.eventType === "FINISHED") {
                setQuizFinished(true);
                endOfQuizButtonData_ref.current = {
                    canPlayAgain: event.data.eventType,
                    showResultsUrl: event.data.showResultsUrl,
                    studentPlannerUrl: event.data.studentPlannerUrl,
                };
            }
            if (event.data?.eventType === "UPDATE_HIGH_SCORE") {
                const { score, quizUuid } = event.data;

                const highScoresStr = localStorage.getItem("high_scores") || "";
                let highScoresJSON: {
                    [quizUuid: string]: [highScore: number];
                } = highScoresStr.length > 0 ? JSON.parse(highScoresStr) : {};

                const currentHighScore = highScoresJSON[quizUuid] || 0;
                const isNewHighScore = score > currentHighScore;
                if (isNewHighScore) {
                    highScoresJSON[quizUuid] = score;
                    localStorage.setItem(
                        "high_scores",
                        JSON.stringify(highScoresJSON)
                    );
                }

                gameIframeRef.current?.contentWindow?.postMessage(
                    {
                        eventType: "HIGH_SCORE",
                        highScore: highScoresJSON[quizUuid],
                        isNewHighScore,
                    },
                    gameUrl
                );
            }
            if (event.data?.eventType === "GAME_OVER") {
                setGameState("LOST");
            }
            if (event.data?.eventType === "WIN") {
                setGameState("WON");
            }

            // Quiz player events
            if (
                ["STARTED", "ANSWERED", "FINISHED"].includes(
                    event.data?.eventType
                )
            ) {
                // Handle when two events are fired at the same time for the same answer
                const now = Date.now();
                if (now - lastAnswerTime.current > MIN_ANSWER_INTERVAL) {
                    if (event.data?.eventType === "STARTED") {
                        console.log("STARTED", event.data);
                        setNumQuizQuestions(event.data.numberOfQuestions);
                    }
                    if (event.data?.eventType === "ANSWERED") {
                        console.log("ANSWERED", event.data);
                        setNumQuizQuestionsAnswered((n) => n + 1);
                        const { correct } = event.data;
                        if (correct) {
                            // gain ammo from answering correctly
                            setAmmo((ammo) => ammo + 5);
                        }
                    }
                    lastAnswerTime.current = now;
                    gameIframeRef.current?.contentWindow?.postMessage(
                        event.data,
                        gameUrl
                    );
                }
            }
        });
    }, [
        numQuizQuestions,
        numQuizQuestionsAnswered,
        setNumQuizQuestionsAnswered,
    ]);

    const endOfQuizButtonData_ref = useRef({
        canPlayAgain: true,
        showResultsUrl: "",
        studentPlannerUrl: "",
    });

    const [quizFinished, setQuizFinished] = React.useState(false);

    const [gameState, setGameState] = React.useState<
        "NOT_STARTED" | "ACTIVE" | "PAUSED" | "WON" | "LOST"
    >("NOT_STARTED");

    const exitUrl =
        endOfQuizButtonData_ref.current?.showResultsUrl ||
        endOfQuizButtonData_ref.current?.studentPlannerUrl;

    const exitGame = () => {
        if (endOfQuizButtonData_ref.current) {
            const url =
                endOfQuizButtonData_ref.current.showResultsUrl ||
                endOfQuizButtonData_ref.current.studentPlannerUrl;
            if (url) {
                window.location.href = url;
            }
        }
    };

    const [quizPlayerUrl, setQuizPlayerUrl] = React.useState("");

    const getQuizPlayerUrlFromParentUrl = () => {
        const DEFAULT_QUIZ_UUID = "a20a8c25-6a51-42ea-9543-303a96ffb0ac"; // Icebreaker quiz
        const quizUuid = extractUUID(window.location.href) || DEFAULT_QUIZ_UUID;

        let iFrameUrl = "";

        const token = getURLQueryParameterByName("token");
        const quizMode = getURLQueryParameterByName("quizMode");
        const origin = window.location.origin;

        const params = [];
        if (token) {
            params.push("token=" + token);
        }
        if (origin) {
            params.push("origin=" + encodeURIComponent(origin));
        }
        if (quizMode) {
            params.push("quizMode=" + quizMode);
        }
        console.log("params", params);

        // actual code for creating url
        if (
            window.location.host.includes("quizalize") ||
            window.location.host.includes("the-sorcerer-wrapper.")
        ) {
            iFrameUrl = `https://player.quizalize.com/quiz/${quizUuid}`;
        }
        if (
            window.location.host.includes("test-") ||
            window.location.host.includes("the-sorcerer-wrapper-test")
        ) {
            iFrameUrl = `https://test-player.quizalize.com/quiz/${quizUuid}`;
        }
        if (window.location.host.includes("localhost")) {
            iFrameUrl = `https://test-player.quizalize.com/quiz/${quizUuid};`;
            // iFrameUrl = `http://localhost:3005/quiz/${quizUuid}`;
        }
        if (params.length > 0) {
            iFrameUrl = iFrameUrl + "?" + params.join("&");
        }

        console.log("iFrameUrl", iFrameUrl);

        setQuizPlayerUrl(iFrameUrl);
    };

    const extractUUID = (url: string) => {
        const matches = url.match(
            /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/
        );
        if (matches) {
            return matches[0];
        }
        return null;
    };

    function getURLQueryParameterByName(
        name: string,
        url = window.location.href
    ) {
        name = name.replace(/[\[\]]/g, "\\$&");
        var regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)"),
            results = regex.exec(url);
        if (!results) return null;
        if (!results[2]) return "";
        return decodeURIComponent(results[2].replace(/\+/g, " "));
    }

    useEffect(() => {
        getQuizPlayerUrlFromParentUrl();
    }, []);

    let gameUrl = "";
    if (
        window.location.host.includes("localhost") ||
        window.location.host.includes("test-")
    ) {
        gameUrl = "https://test-sorcerer-game.quizalize.com/";
    } else {
        gameUrl = "https://sorcerer-game.quizalize.com/";
    }

    // Show either game or quiz, on mobile
    const [showQuizMobile, setShowQuizMobile] = React.useState(true);

    useEffect(() => {
        if (showQuizMobile) {
            quizPlayerContainerRef.current?.classList.add("show");
            gameIframeRef.current?.classList.remove("show");
        } else {
            quizPlayerContainerRef.current?.classList.remove("show");
            gameIframeRef.current?.classList.add("show");
        }
    }, [showQuizMobile]);

    const handleClickResume = () => {
        setGameState("ACTIVE");
        setShowQuizMobile(false);

        // tell game
        gameIframeRef.current?.contentWindow?.postMessage(
            { eventType: "RESUME" },
            gameUrl
        );
    };

    const handleClickPause = () => {
        setGameState("PAUSED");
        setShowQuizMobile(true);

        // tell game
        gameIframeRef.current?.contentWindow?.postMessage(
            { eventType: "PAUSE" },
            gameUrl
        );
    };

    // on mobile
    const handleClickContinue = (e: any) => {
        e.stopPropagation();
        console.log("handleClickContinue");
        setShowQuizMobile(true);
    };

    const handleClickPlayAgain = () => {
        setGameState("ACTIVE");

        // tell game
        gameIframeRef.current?.contentWindow?.postMessage(
            { eventType: "PLAY_AGAIN" },
            gameUrl
        );
    };

    const [showHowToPlay, setShowHowToPlay] = React.useState(false);

    const handleClickHowToPlay = () => {
        setShowHowToPlay(true);
    };

    console.log("gameState", gameState);

    return (
        <div className="App">
            <div className={"games"}>
                <div className={"quiz-player"} ref={quizPlayerContainerRef}>
                    {quizFinished ? (
                        <div className="endOfQuizOptions">
                            <h2>Quiz complete</h2>
                            {/* {endOfQuizButtonData_ref.current.canPlayAgain ? (
                                <div
                                    className="endOfQuizButton playAgain"
                                    onClick={() => window.location.reload()}
                                >
                                    Start the quiz again?
                                </div>
                            ) : null} */}

                            {exitUrl && (
                                <button
                                    className="endOfQuizButton studentPlannerUrl"
                                    onClick={exitGame}
                                >
                                    Exit game
                                </button>
                            )}
                        </div>
                    ) : (
                        <iframe
                            id="inlineFrameQuiz"
                            ref={quizPlayerIframeRef}
                            className={"iframe"}
                            title="Inline Frame Example"
                            width="100%"
                            height="100%"
                            src={quizPlayerUrl}
                        />
                    )}
                </div>
                <div
                    className={`game-container ${
                        showQuizMobile ? "mobile-hidden" : ""
                    }`}
                >
                    <iframe
                        id="game"
                        ref={gameIframeRef}
                        className={"iframe game"}
                        title="Inline Frame Example"
                        width="100%"
                        height="100%"
                        src={gameUrl}
                    />

                    {/* Desktop bottom bar */}
                    {gameState === "NOT_STARTED" && (
                        <div className="bottom-bar desktop">
                            <div className="title">
                                Start the quiz to begin the game!
                            </div>
                            <div
                                className="how-to-play-btn"
                                onClick={handleClickHowToPlay}
                            >
                                How to play
                            </div>
                        </div>
                    )}
                    {["ACTIVE", "PAUSED"].includes(gameState) && (
                        <div className="bottom-bar desktop">
                            {
                                <div className="row">
                                    <div className={"column"}>
                                        <div className={"subtitle"}>
                                            Answer questions to gain ammo
                                        </div>
                                    </div>
                                </div>
                            }

                            {gameState === "PAUSED" ? (
                                <button
                                    className={
                                        "resume-btn desktop " +
                                        (ammo < 1 ? " disabled" : "")
                                    }
                                    disabled={ammo < 1}
                                    onClick={handleClickResume}
                                >
                                    Resume game
                                </button>
                            ) : (
                                <button
                                    className={"resume-btn desktop"}
                                    onClick={handleClickPause}
                                >
                                    Pause game
                                </button>
                            )}

                            <div
                                className="how-to-play-btn"
                                onClick={handleClickHowToPlay}
                            >
                                How to play
                            </div>
                        </div>
                    )}
                    {gameState === "WON" && (
                        <div className="bottom-bar desktop"></div>
                    )}
                    {gameState === "LOST" && (
                        <div className="bottom-bar desktop">
                            {numQuizQuestionsAnswered <
                                (numQuizQuestions || 0) && (
                                <button
                                    className={"resume-btn desktop"}
                                    onClick={handleClickPlayAgain}
                                >
                                    Play again
                                </button>
                            )}
                        </div>
                    )}
                </div>
                {showHowToPlay && (
                    <div className="modal-bg">
                        <div className="how-to-play-modal">
                            <div
                                className="close-btn"
                                onClick={() => setShowHowToPlay(false)}
                            >
                                X
                            </div>
                            <div className="how-to-play-title">HOW TO PLAY</div>
                            <div className="how-to-play-content">
                                <p>
                                    Shoot balls of the same colour to destroy
                                    them.
                                </p>
                                <img src={howToGif} className="how-to-gif" />

                                <p>Answer questions to get ammo.</p>
                                <p>Destroy all balls to win the game!</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Mobile bottom bar */}
                {showQuizMobile && gameState === "NOT_STARTED" && (
                    <div className={"bottom-bar mobile"}>
                        <div className={"column"}>
                            <div className={"subtitle"}>
                                Start the quiz to begin the game
                            </div>
                        </div>
                    </div>
                )}
                {showQuizMobile && gameState === "PAUSED" && (
                    <div className={"bottom-bar mobile"}>
                        <div className="row">
                            <div className={"title"}>GAME PAUSED</div>
                            <div className={"column"}>
                                <div className="ammo">{ammo} ammo</div>
                                Answer questions to gain ammo in the game
                                <div className={"subtitle"}></div>
                            </div>
                        </div>
                        <button
                            className={
                                "resume-btn" + (ammo < 1 ? " disabled" : "")
                            }
                            disabled={ammo < 1}
                            onClick={handleClickResume}
                        >
                            Resume
                        </button>
                    </div>
                )}
                {showQuizMobile && gameState === "ACTIVE" && (
                    <div
                        className={"bottom-bar mobile"}
                        onClick={handleClickResume}
                    >
                        <div className="row">
                            <div className={"column"}>
                                <div className="ammo">{ammo} ammo</div>
                                Answer questions to gain ammo in the game
                                <div className={"subtitle"}></div>
                            </div>
                        </div>
                        <button
                            className={
                                "resume-btn" + (ammo < 1 ? " disabled" : "")
                            }
                            disabled={ammo < 1}
                            onClick={handleClickResume}
                        >
                            Enter game
                        </button>
                    </div>
                )}
                {!showQuizMobile && gameState === "ACTIVE" && (
                    <div
                        className={"bottom-bar mobile"}
                        onClick={handleClickPause}
                    >
                        <div className={"subtitle"}>
                            Answer questions to gain ammo
                        </div>
                        The game will be paused while the quiz is open
                        <div className={"resume-btn"}>Open quiz</div>
                    </div>
                )}
                {!showQuizMobile && ["WON", "LOST"].includes(gameState) && (
                    <div
                        className={"bottom-bar mobile"}
                        onClick={handleClickContinue}
                    >
                        <div className={"resume-btn"}>Continue</div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default App;
